import { SERVICE_REGIONS } from '@felix/domain';
import { Button, Checkbox, Modal, Select, Skeleton, TextInput } from '@mantine/core';
import { IconRosetteDiscountCheck, IconStarFilled, IconUserPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import { useCountryName } from '../ports/catalog-context.js';
import classes from './RfqMarket.module.css';
import { ALL_REGIONS, suppliersForPublish, validateMarketFields } from './rfq-regions.js';
import type { RfqSource } from './source.js';
import type { Partner, RfqDetail, Supplier } from './types.js';

/**
 * Açık RFQ pazar yeri ve bölge süzgecinin ofis parçaları (ADR 0113): teslim bölgesi seçici,
 * bölgeye göre süzülen davet listesi, tedarikçinin bölgeleri ve "New supplier" rozeti.
 */

const useM = () => {
  const { t } = useTranslation();
  return (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasingMarket.${key}`, options);
};

/** Bölgelerin seçenekleri (dile göre ad). */
export function useRegionOptions() {
  const m = useM();
  return SERVICE_REGIONS.map((code) => ({ value: code, label: m(`regions.${code}`) }));
}

/** Teslim bölgesi ("açık" yayında zorunlu). */
export function RegionSelect({
  value,
  onChange,
  error,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  error?: string | undefined;
}) {
  const m = useM();
  return (
    <Select
      label={m('deliveryRegion')}
      description={m('deliveryRegionHint')}
      placeholder={m('optional')}
      data={useRegionOptions()}
      value={value}
      onChange={onChange}
      error={error}
      clearable
      searchable
      comboboxProps={{ withinPortal: false }}
    />
  );
}

/** Tedarikçinin bölgeleri ("Southeast Asia · Far East"); boşsa "Region not set". */
export function RegionList({ regions }: { regions: readonly string[] | undefined }) {
  const m = useM();
  if (!regions || regions.length === 0) {
    return <span className={`${classes.regions} ${classes.unset}`}>{m('regionUnset')}</span>;
  }
  return (
    <span className={classes.regions}>
      {regions.map((code) => m(`regions.${code}`, { defaultValue: code })).join(' · ')}
    </span>
  );
}

/** Açık pazarda teklif veren, ofisin henüz onaylamadığı tedarikçi. */
export function NewSupplierBadge() {
  const m = useM();
  return (
    <StatusBadge tone="warning" icon={<IconUserPlus size={12} />}>
      {m('newSupplier')}
    </StatusBadge>
  );
}

/**
 * Davetli yayında yayın anında davet edilecek tedarikçiler: bölge süzgeci ve onaylı, etkin,
 * henüz davet edilmemiş tedarikçilerin onay kutuları.
 */
export function InviteSuppliersField({
  suppliers,
  invitedIds,
  region,
  onRegion,
  picked,
  onPicked,
  error,
}: {
  suppliers: readonly Supplier[];
  invitedIds: readonly string[];
  region: string;
  onRegion: (region: string) => void;
  picked: readonly string[];
  onPicked: (ids: string[]) => void;
  error?: string | undefined;
}) {
  const m = useM();
  const options = useRegionOptions();
  const visible = suppliersForPublish(suppliers, { invitedIds, region });
  return (
    <fieldset className={classes.invite}>
      <legend>{m('inviteLegend')}</legend>
      <div className={classes.inviteHead}>
        <Select
          label={m('regionFilter')}
          data={[{ value: ALL_REGIONS, label: m('allRegions') }, ...options]}
          value={region}
          onChange={(value) => onRegion(value ?? ALL_REGIONS)}
          allowDeselect={false}
          searchable
          comboboxProps={{ withinPortal: false }}
        />
        <span className={classes.count} aria-live="polite">
          {m('selected', { count: picked.length })}
        </span>
      </div>
      {visible.length === 0 ? (
        <p className={classes.empty}>
          {region === ALL_REGIONS ? m('noSuppliers') : m('noSuppliersInRegion')}
        </p>
      ) : (
        <Checkbox.Group value={[...picked]} onChange={onPicked} aria-label={m('inviteLegend')}>
          <ul className={classes.list}>
            {visible.map((supplier) => (
              <li key={supplier.id} className={classes.item}>
                <Checkbox
                  value={supplier.id}
                  label={supplier.name}
                  description={<RegionList regions={supplier.serviceRegions} />}
                />
              </li>
            ))}
          </ul>
        </Checkbox.Group>
      )}
      {error ? (
        <p className={classes.hint} role="alert" data-error>
          {m(`fieldErrors.${error}`)}
        </p>
      ) : (
        <p className={classes.hint}>{m('inviteHint')}</p>
      )}
    </fieldset>
  );
}

/** Partner dizini sonuçları: işaretlenen hesaplar davetli olur (ADR 0113 §5). */
export function PartnerResults({
  items,
  picked,
  onPicked,
}: {
  items: readonly Partner[];
  picked: readonly string[];
  onPicked: (ids: string[]) => void;
}) {
  const m = useM();
  const country = useCountryName();
  if (items.length === 0) return <p className={classes.empty}>{m('noPartners')}</p>;
  return (
    <Checkbox.Group value={[...picked]} onChange={onPicked} aria-label={m('partnersLegend')}>
      <ul className={classes.list}>
        {items.map((partner) => (
          <li key={partner.accountId} className={classes.item}>
            <Checkbox
              value={partner.accountId}
              label={
                <span className={classes.partnerName}>
                  {partner.companyName}
                  {partner.supplierId ? (
                    <StatusBadge tone="success">{m('yourSupplier')}</StatusBadge>
                  ) : null}
                  {partner.verified ? (
                    <StatusBadge
                      tone="info"
                      icon={<IconRosetteDiscountCheck size={12} aria-hidden />}
                    >
                      {m('verified')}
                    </StatusBadge>
                  ) : null}
                  {partner.rating ? (
                    <span className={classes.rating}>
                      <IconStarFilled size={12} aria-hidden />
                      {m('rating', {
                        average: partner.rating.average.toFixed(1),
                        count: partner.rating.count,
                      })}
                    </span>
                  ) : null}
                </span>
              }
              description={
                <span className={classes.regions}>
                  {[partner.country ? country(partner.country) : null, partner.contactName]
                    .filter(Boolean)
                    .join(' · ')}
                  {' · '}
                  <RegionList regions={partner.regions} />
                </span>
              }
            />
          </li>
        ))}
      </ul>
    </Checkbox.Group>
  );
}

/** Partner dizini araması: ad ya da liman ve bölgeyle; sonuçlar işaretlenerek davet edilir. */
function PartnerSearch({
  source,
  picked,
  onPicked,
}: {
  source: RfqSource;
  picked: readonly string[];
  onPicked: (ids: string[]) => void;
}) {
  const m = useM();
  const options = useRegionOptions();
  const [q, setQ] = useState('');
  const [region, setRegion] = useState<string>(ALL_REGIONS);
  const [items, setItems] = useState<Partner[] | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!source.partners) return;
    setBusy(true);
    try {
      const found = await source.partners({
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(region !== ALL_REGIONS ? { region } : {}),
      });
      setItems(found.items);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <fieldset className={classes.invite}>
      <legend>{m('partnersLegend')}</legend>
      <div className={classes.searchRow}>
        <TextInput
          label={m('partnerSearch')}
          placeholder={m('partnerSearchPlaceholder')}
          value={q}
          onChange={(event) => setQ(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void run();
            }
          }}
        />
        <Select
          label={m('regionFilter')}
          data={[{ value: ALL_REGIONS, label: m('allRegions') }, ...options]}
          value={region}
          onChange={(value) => setRegion(value ?? ALL_REGIONS)}
          allowDeselect={false}
          searchable
          comboboxProps={{ withinPortal: false }}
        />
        <Button variant="light" loading={busy} onClick={() => void run()}>
          {m('search')}
        </Button>
      </div>
      {items === null ? (
        <p className={classes.hint}>{m('partnersHint')}</p>
      ) : (
        <PartnerResults items={items} picked={picked} onPicked={onPicked} />
      )}
      {picked.length ? (
        <span className={classes.count} aria-live="polite">
          {m('selected', { count: picked.length })}
        </span>
      ) : null}
    </fieldset>
  );
}

/**
 * Yayın penceresinin açık pazar parçası (ADR 0113 §2, §5): "açık"ta teslim bölgesi, davetlide
 * bölge süzgeçli davet listesi. Pencere görünürlüğü verir; doğrulama ve gövde buradan gelir.
 */
export function usePublishMarket(detail: RfqDetail, source: RfqSource) {
  const m = useM();
  const [region, setRegion] = useState<string | null>(detail.deliveryRegion ?? null);
  const [filter, setFilter] = useState<string>(ALL_REGIONS);
  const [picked, setPicked] = useState<string[]>([]);
  const [partners, setPartners] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    source
      .suppliers()
      .then((list) => {
        if (alive) setSuppliers(list.items);
      })
      .catch(() => {
        if (alive) setSuppliers([]);
      });
    return () => {
      alive = false;
    };
  }, [source]);
  const invitedIds = detail.suppliers.map((s) => s.id);
  const names = detail.suppliers.map((s) => s.name).join(', ');
  return {
    validate(visibility: 'open' | 'invited') {
      const found = validateMarketFields({
        visibility,
        invited: invitedIds.length,
        picked: picked.length,
        partners: partners.length,
      });
      setErrors(found);
      return found;
    },
    payload(visibility: 'open' | 'invited') {
      return visibility === 'open'
        ? { deliveryRegion: region }
        : { deliveryRegion: region, supplierIds: picked, partnerAccountIds: partners };
    },
    render(visibility: 'open' | 'invited') {
      if (visibility === 'open') {
        return (
          <RegionSelect
            value={region}
            onChange={(value) => {
              setRegion(value);
              setErrors({});
            }}
            error={errors.deliveryRegion ? m(`fieldErrors.${errors.deliveryRegion}`) : undefined}
          />
        );
      }
      return (
        <>
          {names ? <p className={classes.hint}>{m('alreadyInvited', { names })}</p> : null}
          {suppliers === null ? (
            <Skeleton height={120} />
          ) : (
            <InviteSuppliersField
              suppliers={suppliers}
              invitedIds={invitedIds}
              region={filter}
              onRegion={setFilter}
              picked={picked}
              onPicked={(ids) => {
                setPicked(ids);
                setErrors({});
              }}
              error={errors.suppliers}
            />
          )}
          {source.partners ? (
            <PartnerSearch
              source={source}
              picked={partners}
              onPicked={(ids) => {
                setPartners(ids);
                setErrors({});
              }}
            />
          ) : null}
        </>
      );
    },
  };
}

/**
 * Açık pazardaki aday tedarikçiyi şirket tedarikçisi yapma onayı (ADR 0113 §4): kart onaylanır,
 * portal bağı etkinleşir.
 */
export function AddSupplierDialog({
  name,
  onClose,
  onConfirm,
}: {
  name: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const m = useM();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      opened
      onClose={onClose}
      title={m('addSupplierTitle', { name })}
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <p className={classes.hint}>{m('addSupplierText')}</p>
      {error ? (
        <p className={classes.hint} role="alert" data-error>
          {t(`purchasing.errors.${error}`, { defaultValue: t('purchasing.errors.GENERIC') })}
        </p>
      ) : null}
      <div className={classes.actions}>
        <Button variant="default" onClick={onClose}>
          {t('purchasing.password.back')}
        </Button>
        <Button
          loading={busy}
          leftSection={<IconUserPlus size={16} />}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onConfirm();
            } catch (failure) {
              setError(errorCode(failure));
              setBusy(false);
            }
          }}
        >
          {m('addSupplierConfirm')}
        </Button>
      </div>
    </Modal>
  );
}
